import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { interval, switchMap, catchError, of, startWith, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DashboardData, EVGData, XylemDevice, Alarm } from '../models/dashboard-data.model';

interface AwsLiveDataResponse {
  station_id: string;
  polled_at: string;
  online?: string;
  data_points: string; // JSON-encoded array with snake_case keys
  alarms?: any[];
  alarm_count?: number;
}

interface AwsHistoricalDataResponse {
  station_id: string;
  count: number;
  alarms?: any[];
  alarm_count?: number;
  data: AwsLiveDataResponse[];
}


/**
 * Dashboard Data Service
 * Handles live data polling with 45-second intervals
 * Uses Angular Signals for reactive state management
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  // Countdown timer for next update
  private countdownValue = signal(Math.round(environment.polling.liveDataInterval / 1000));
  private lastUpdateTime = signal<Date>(new Date());
  
  // Connection status
  connectionStatus = signal<'connected' | 'disconnected' | 'error'>('disconnected');
  
  // Error tracking
  errorMessage = signal<string | null>(null);
  
  // Raw data signal
  private rawData = signal<XylemDevice[] | null>(null);

  // Alarms from latest live response
  alarms = signal<Alarm[]>([]);
  alarmCount = signal<number>(0);
  
  // Historical data storage for charts (rolling window of last 20 data points)
  private historicalData = signal<Array<{
    timestamp: Date;
    devices: XylemDevice[];
  }>>([]);
  
  // Transform raw Xylem API response to DashboardData format
  currentData = computed(() => {
    const raw = this.rawData();
    if (!raw) return null;
    
    return {
      devices: raw,
      timestamp: new Date()
    } as DashboardData;
  });
  
  private readonly SESSION_KEY = 'wedeco_live_history';

  constructor(private http: HttpClient) {
    // Restore historical data from sessionStorage so chart shows immediately on reload
    try {
      const stored = sessionStorage.getItem(this.SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Array<{ timestamp: string; devices: XylemDevice[] }>;
        this.historicalData.set(parsed.map(h => ({ ...h, timestamp: new Date(h.timestamp) })));
      }
    } catch { /* ignore corrupt/quota errors */ }

    // Live data polling
    interval(environment.polling.liveDataInterval).pipe(
      startWith(0),
      switchMap(() => this.http.get<AwsLiveDataResponse>(environment.aws.liveData, {
        params: new HttpParams().set('station_id', environment.xylem.stationId)
      }).pipe(
        catchError(error => {
          if (!error.message?.includes('OIDC token')) {
            this.connectionStatus.set('error');
            this.errorMessage.set(error.message || 'Failed to fetch live data');
            console.error('Live data polling error:', error);
          }
          return of(null);
        })
      ))
    ).subscribe(response => {
      if (response) {
        const data = this.transformLiveResponse(response);
        this.rawData.set(data);
        this.alarms.set((response.alarms ?? []) as Alarm[]);
        this.alarmCount.set(response.alarm_count ?? 0);
        this.lastUpdateTime.set(new Date());
        this.connectionStatus.set('connected');
        this.errorMessage.set(null);

        // Always append to history (offline polls create gaps; online polls show data)
        const history = this.historicalData();
        const newHistory = [...history, { timestamp: this.getSnapshotTimestamp(data), devices: data }];
        if (newHistory.length > 20) {
          newHistory.shift();
        }
        this.historicalData.set(newHistory);
        try {
          sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(newHistory));
        } catch { /* ignore quota errors */ }
      }
    });
    
    // Countdown timer Observable (updates every second)
    interval(1000).subscribe(() => {
      const now = new Date();
      const lastUpdate = this.lastUpdateTime();
      const secondsElapsed = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);
      const remainingSeconds = Math.max(0, Math.round(environment.polling.liveDataInterval / 1000) - secondsElapsed);
      this.countdownValue.set(remainingSeconds);
    });
  }
  
  /**
   * Get countdown value (seconds until next update)
   */
  getCountdown = computed(() => this.countdownValue());
  
  /**
   * Get formatted countdown string
   */
  getCountdownFormatted = computed(() => {
    const seconds = this.countdownValue();
    return `Next update in ${seconds}s`;
  });

  /**
   * Get last update time formatted as HH:MM:SS
   */
  getLastUpdateFormatted = computed(() => {
    const t = this.lastUpdateTime();
    return t.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  });

  /** True if any device in the current poll is online */
  isDeviceOnline = computed(() => {
    const data = this.currentData();
    if (!data?.devices?.length) return false;
    return data.devices.some(d => d.online);
  });

  /** Formatted timestamp of the most recent data point across all groups */
  getDeviceLastSeenFormatted = computed(() => {
    const data = this.currentData();
    if (!data?.devices?.length) return null;
    const allTimestamps = data.devices
      .flatMap(d => d.deviceData?.groups ?? [])
      .map(g => new Date(g.timestamp).getTime())
      .filter(t => !isNaN(t));
    if (!allTimestamps.length) return null;
    const maxTs = Math.max(...allTimestamps);
    return new Date(maxTs).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  });

  /**
   * Manual refresh - fetch data immediately
   */
  async manualRefresh(): Promise<void> {
    try {
      this.connectionStatus.set('connected');
      const raw = await firstValueFrom(this.http.get<AwsLiveDataResponse>(environment.aws.liveData, {
        params: new HttpParams().set('station_id', environment.xylem.stationId)
      }));
      const data = this.transformLiveResponse(raw);
      if (data) {
        this.rawData.set(data);
        
        const history = this.historicalData();
        const newHistory = [...history, { timestamp: this.getSnapshotTimestamp(data), devices: data }];
        if (newHistory.length > 20) newHistory.shift();
        this.historicalData.set(newHistory);
      }
      this.lastUpdateTime.set(new Date());
      this.countdownValue.set(Math.round(environment.polling.liveDataInterval / 1000));
      this.errorMessage.set(null);
    } catch (error: any) {
      if (error.message?.includes('OIDC token')) return;
      this.connectionStatus.set('error');
      this.errorMessage.set(error.message || 'Failed to refresh data');
      console.error('Manual refresh error:', error);
    }
  }
  
  /**
   * Get historical data for charts
   */
  getHistoricalData = computed(() => this.historicalData());
  
  /**
   * Fetch historical time-series data from API
   * @param from - ISO date string (e.g. 2026-04-02T00:00:00)
   * @param to - ISO date string (e.g. 2026-04-02T23:59:59)
   */
  async fetchHistoricalTimeseries(from: string, to: string): Promise<Array<{ timestamp: Date; devices: XylemDevice[] }>> {
    const params = new HttpParams()
      .set('station_id', environment.xylem.stationId)
      .set('from', from)
      .set('to', to);
    const response = await firstValueFrom(
      this.http.get<AwsHistoricalDataResponse>(environment.aws.historicalData, { params })
    );
    return response.data.map(entry => {
      const devices = this.transformLiveResponse(entry);
      return { timestamp: this.getSnapshotTimestamp(devices, new Date(entry.polled_at)), devices };
    });
  }
  
  /**
   * Get EVG data for specific EVG number
   */
  getEVGData(evgNumber: 1 | 2 | 3 | 4 | 5 | 6) {
    return computed(() => {
      const data = this.currentData();
      if (!data?.devices || data.devices.length === 0) return null;
      return this.extractEVGData(data.devices[0], evgNumber);
    });
  }
  
  /**
   * Get system vitals
   */
  getSystemVitals = computed(() => {
    const data = this.currentData();
    if (!data?.devices) return null;

    const devices = data.devices;
    const onlineDevices = devices.filter(d => d.online).length;
    
    let totalTemp = 0;
    let totalVoltage = 0;
    let totalCurrent = 0;
    let totalPower = 0;
    let count = 0;

    devices.forEach(device => {
      for (let i = 1; i <= 6; i++) {
        const evg = this.extractEVGData(device, i);
        if (evg) {
          totalTemp += evg.temperature;
          totalVoltage += evg.voltage;
          totalCurrent += evg.current;
          totalPower += evg.powerLevel;
          count++;
        }
      }
    });

    return {
      totalDevices: devices.length,
      onlineDevices,
      averageTemperature: count > 0 ? totalTemp / count : 0,
      averageVoltage: count > 0 ? totalVoltage / count : 0,
      averageCurrent: count > 0 ? totalCurrent / count : 0,
      averagePowerLevel: count > 0 ? totalPower / count : 0,
      lastUpdate: data.timestamp
    };
  });
  
  /**
   * Get UV sensors data - Not available in Xylem API
   */
  getUVSensors = computed(() => {
    return [];
  });
  
  /**
   * Get system main state - Not available in Xylem API  
   */
  getSystemState = computed(() => {
    const data = this.currentData();
    const onlineDevices = data?.devices?.filter(d => d.online).length || 0;
    return onlineDevices > 0 ? 'System Running' : 'Standby';
  });

  /**
   * Transform AWS live-data response into XylemDevice[] expected by the rest of the app.
   * data_points is a JSON-encoded string of flat data point objects.
   */
  private transformLiveResponse(response: AwsLiveDataResponse): XylemDevice[] {
    const raw: any[] = JSON.parse(response.data_points);
    return raw.map(d => ({
      id: d.id,
      name: d.name,
      type: d.type,
      online: d.online,
      deviceData: {
        label: d.devicedata?.label ?? '',
        groups: (d.devicedata?.groups ?? []).map((g: any) => ({
          name: g.name,
          label: g.label,
          icon: g.icon ?? '',
          timestamp: g.timestamp,
          values: (g.values ?? []).map((v: any) => ({
            dataPointId: v.datapointid,
            name: v.name,
            label: v.label,
            timestamp: v.timestamp,
            dataType: v.datatype ?? 'INT64',
            value: v.value,
            valueLabel: v.valuelabel,
            unit: v.unit,
            accessMode: v.accessmode ?? 'READABLE'
          }))
        }))
      }
    }));
  }

  private getSnapshotTimestamp(devices: XylemDevice[], fallback: Date = new Date()): Date {
    const timestamps = devices
      .flatMap(d => d.deviceData?.groups ?? [])
      .map(g => new Date(g.timestamp).getTime())
      .filter(t => !isNaN(t));
    return timestamps.length ? new Date(Math.max(...timestamps)) : fallback;
  }

  /**
   * Extract EVG data from a specific ballast in device data
   * Maps ballast1-6 to corresponding device data groups
   */
  extractEVGData(device: XylemDevice, ballastNumber: number): EVGData | null {
    if (!device.deviceData?.groups) return null;

    const groups = device.deviceData.groups;
    
    // Pattern mapping:
    // ballast1: status 1,2   voltage/current/temp 1   pwm 1
    // ballast2: status 3,4   voltage/current/temp 2   pwm 2
    // ballast3: status 5,6   voltage/current/temp 3   pwm 3
    // etc.
    const statusByte1Index = (ballastNumber - 1) * 2 + 1;
    const statusByte2Index = (ballastNumber - 1) * 2 + 2;
    
    // Find data points for this ballast
    const statusByte1Group = groups.find(g => g.name === `device-status-running-${statusByte1Index}`);
    const statusByte2Group = groups.find(g => g.name === `device-status-running-${statusByte2Index}`);
    const voltageGroup = groups.find(g => g.name === `device-voltage-system-${ballastNumber}`);
    const currentGroup = groups.find(g => g.name === `device-current-system-${ballastNumber}`);
    const tempGroup = groups.find(g => g.name === `device-temperature-system-${ballastNumber}`);
    const pwmGroup = groups.find(g => g.name === `device-percentage-level-${ballastNumber}`);

    // Determine running status from Byte2 bits: bit0=ballast_ok, bit1=ballast_ready
    const statusByte2Value = statusByte2Group?.values[0]?.value ?? 0;
    const isRunning = (statusByte2Value & 0x03) === 3;

    // Decode lamp status from Byte2: bits 5-4 = lamp1, bits 7-6 = lamp2 ('01' = ON)
    const lamp1On = ((statusByte2Value >> 4) & 0x03) === 0x01;
    const lamp2On = ((statusByte2Value >> 6) & 0x03) === 0x01;

    return {
      evgId: ballastNumber,
      status: isRunning ? 'Running' : 'Not running',
      voltage: voltageGroup?.values[0]?.value || 0,
      current: currentGroup?.values[0]?.value || 0,
      temperature: tempGroup?.values[0]?.value || 0,
      powerLevel: pwmGroup?.values[0]?.value || 0,
      running: isRunning,
      lamp1_status: lamp1On,
      lamp2_status: lamp2On,
      timestamp: statusByte2Group?.values[0]?.timestamp
              || statusByte1Group?.values[0]?.timestamp
              || new Date().toISOString()
    };
  }
}
